import React, { useState } from 'react';

const DASHBOARDS = [
  {
    id: 'gnl-portal',
    title: 'Healthcare Interpretation Portal',
    subtitle: 'GNL Interpreters Real-Time Operational Console',
    category: 'Medical WebRTC Platform',
    metrics: [
      { label: 'Active Call Sessions', val: '14 Live' },
      { label: 'Avg Dispatch Time', val: '8.4 sec' },
      { label: 'Active Interpreters', val: '142 Online' },
      { label: 'Compliance Status', val: 'HIPAA Verified' },
    ],
    previewItems: [
      { col1: 'Call #4920 - Spanish (VRI)', col2: 'St. Jude Hospital', col3: '08:42 mins', status: 'In Progress' },
      { col1: 'Call #4921 - ASL (Video)', col2: 'Metro Health Clinic', col3: '03:15 mins', status: 'Connecting' },
      { col1: 'Call #4922 - Vietnamese (OPI)', col2: 'Community Care', col3: '14:20 mins', status: 'In Progress' },
      { col1: 'Call #4923 - Mandarin (VRI)', col2: 'General Hospital', col3: '01:05 mins', status: 'Matched' },
    ],
  },
  {
    id: 'restaurant-analytics',
    title: 'Restaurant Cost & Inventory CRM',
    subtitle: 'Real-Time Operational Cost & Profit Analytics',
    category: 'Hospitality Tech',
    metrics: [
      { label: 'Weekly Food Spend', val: '$4,280' },
      { label: 'Estimated Waste Saved', val: '$840' },
      { label: 'Top Margin Item', val: 'Wagyu Burger (68%)' },
      { label: 'AI Re-Engagements', val: '184 Sent' },
    ],
    previewItems: [
      { col1: 'Supplier: Sysco Prime Meat', col2: '$1,420 Invoice', col3: 'PDF Auto-Parsed', status: 'Synced to Ledger' },
      { col1: 'Promo: Weekend Brunch SMS', col2: '42 Guests Visited', col3: '$1,840 Revenue', status: '+24% Lift' },
      { col1: 'Waste Alert: Produce Surplus', col2: 'Avocado Waste High', col3: 'Menu Rec: Specials', status: 'AI Suggestion' },
    ],
  },
  {
    id: 'cloud-finops',
    title: 'Cloud FinOps & Cost Dashboard',
    subtitle: 'AWS & Multi-Cloud Right-Sizing Dashboard',
    category: 'Cloud Infrastructure',
    metrics: [
      { label: 'Monthly AWS Bill', val: '$2,850' },
      { label: 'FinOps Cost Reduction', val: '38.5%' },
      { label: 'EC2 Right-Sizing Alerts', val: '4 Found' },
      { label: 'Reserved Inst Savings', val: '$620 / mo' },
    ],
    previewItems: [
      { col1: 'prod-api-cluster (us-east-1)', col2: 't3.xlarge ➔ t3.large', col3: 'Save $140/mo', status: 'Action Ready' },
      { col1: 'dev-database-rds-replica', col2: 'Idle 82% of time', col3: 'Auto-Stop Schedule', status: 'Applied' },
      { col1: 'S3 Storage Lifecycle Policy', col2: 'Archive 1.2TB Old Logs', col3: 'Save $45/mo', status: 'Active' },
    ],
  },
];

const DashboardPreviews: React.FC = () => {
  const [activeId, setActiveId] = useState<string>('gnl-portal');
  const activeDash = DASHBOARDS.find((d) => d.id === activeId) || DASHBOARDS[0];

  return (
    <section id="dashboards" className="py-24 bg-slate-900 text-white relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12">
          <p className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-3">
            Interactive System Interfaces
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
            Don’t Take Our Word For It — <span className="text-blue-400">See the Dashboards We Build</span>
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed">
            Every system we engineer comes with intuitive, real-time control panels designed for high operational visibility.
          </p>
        </div>

        {/* Tab Selection */}
        <div className="flex flex-wrap gap-3 mb-8">
          {DASHBOARDS.map((d) => (
            <button
              key={d.id}
              onClick={() => setActiveId(d.id)}
              className={`px-5 py-3 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 border ${
                activeId === d.id
                  ? 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-600/30'
                  : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {d.title}
            </button>
          ))}
        </div>

        {/* Simulated Dashboard UI Window */}
        <div className="bg-slate-950 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl">
          {/* Window Header Bar */}
          <div className="bg-slate-900 px-6 py-4 border-b border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-500/80 inline-block" />
              <span className="w-3 h-3 rounded-full bg-yellow-500/80 inline-block" />
              <span className="w-3 h-3 rounded-full bg-green-500/80 inline-block" />
              <span className="ml-4 font-mono text-xs text-slate-400 hidden sm:inline">
                https://admin.cloudignite.io/live-dashboard/{activeDash.id}
              </span>
            </div>
            <span className="px-3 py-1 bg-blue-500/10 text-blue-400 text-xs font-semibold rounded-full border border-blue-500/20">
              {activeDash.category}
            </span>
          </div>

          {/* Dashboard Body */}
          <div className="p-6 sm:p-8 space-y-6">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-1">{activeDash.title}</h3>
              <p className="text-slate-400 text-xs sm:text-sm">{activeDash.subtitle}</p>
            </div>

            {/* Metrics Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {activeDash.metrics.map((m) => (
                <div key={m.label} className="bg-slate-900/90 border border-slate-800 rounded-2xl p-4">
                  <p className="text-slate-400 text-xs mb-1 font-medium">{m.label}</p>
                  <p className="text-lg sm:text-xl font-bold text-blue-400 font-mono">{m.val}</p>
                </div>
              ))}
            </div>

            {/* Simulated Data Table */}
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl overflow-hidden">
              <div className="px-6 py-3 bg-slate-900 border-b border-slate-800 text-xs font-semibold uppercase tracking-wider text-slate-400 flex justify-between">
                <span>Real-Time Activity Stream</span>
                <span className="text-emerald-400">● Live Updates</span>
              </div>
              <div className="divide-y divide-slate-800/60">
                {activeDash.previewItems.map((item, idx) => (
                  <div key={idx} className="px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs sm:text-sm hover:bg-slate-800/40 transition-colors">
                    <div>
                      <span className="font-semibold text-white block sm:inline">{item.col1}</span>
                      <span className="text-slate-400 sm:ml-4 text-xs block sm:inline">{item.col2}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-slate-300 text-xs">{item.col3}</span>
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-300 border border-blue-500/20">
                        {item.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashboardPreviews;
